import {
    CompareArrowsRounded,
    TrendingDownRounded,
    TrendingFlatRounded,
    TrendingUpRounded,
} from "@mui/icons-material";
import { Box, Chip, CircularProgress, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import {
    KboFlowDebugService,
    KboFlowStepDebugExecuteResult,
    KboFlowStepDebugJudgement,
} from "services/KboFlowDebugService";
import { KboFlowStepDebugSkill } from "services/KboFlowStepDebugService";
import { shortId } from "../KboFlowDebug/KboFlowDebugUI";
import { FlowStep } from "./FlowStepCatalog";
import { SkillDrafts } from "./FlowStepDebugSkills";
import { SectionCard, SectionCardHeader } from "./FlowStepDebugUI";
import { countEditedLines } from "./lineDiff";

/**
 * "Evaluation" stage: what the skill edits changed, and an LLM judge's read on whether the step's
 * output got better. One judge call per edited skill, because each skill has its own judge prompt.
 */

/** An edited skill paired with its before and after instructions. */
interface EditedSkill {
    skill: KboFlowStepDebugSkill;
    original: string;
    draft: string;
    edits: number;
}

const VERDICT_PRESENTATION: Record<
    string,
    { label: string; color: "success" | "error" | "default"; icon: React.ReactElement }
> = {
    improved: {
        label: "Improved",
        color: "success",
        icon: <TrendingUpRounded fontSize="small" />,
    },
    regressed: {
        label: "Regressed",
        color: "error",
        icon: <TrendingDownRounded fontSize="small" />,
    },
    unchanged: {
        label: "Unchanged",
        color: "default",
        icon: <TrendingFlatRounded fontSize="small" />,
    },
};

function SkillChangesCard({ edited }: { edited: EditedSkill }) {
    const theme = useTheme();
    const removed = edited.original
        .split("\n")
        .filter((line) => !new Set(edited.draft.split("\n")).has(line)).length;
    const added = edited.draft
        .split("\n")
        .filter((line) => !new Set(edited.original.split("\n")).has(line)).length;

    return (
        <SectionCard>
            <Box
                sx={{
                    alignItems: "center",
                    borderBottom: "1px solid",
                    borderColor: theme.palette.gray[200],
                    display: "flex",
                    gap: 1,
                    px: 2.5,
                    py: 2,
                }}
            >
                <Typography sx={{ fontSize: 16, fontWeight: 700, minWidth: 0 }}>
                    {edited.skill.name ?? "Unnamed skill"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {removed > 0 && (
                    <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={`−${removed} removed`}
                        sx={{ fontWeight: 700 }}
                    />
                )}
                {added > 0 && (
                    <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={`+${added} added`}
                        sx={{ fontWeight: 700 }}
                    />
                )}
            </Box>
            <Box sx={{ fontSize: 13, maxHeight: 420, overflow: "auto" }}>
                <ReactDiffViewer
                    oldValue={edited.original}
                    newValue={edited.draft}
                    splitView={false}
                    compareMethod={DiffMethod.WORDS}
                    showDiffOnly
                />
            </Box>
        </SectionCard>
    );
}

function JudgeSummaryCard({
    skillName,
    judgement,
    running,
}: {
    skillName: string;
    judgement?: KboFlowStepDebugJudgement;
    running: boolean;
}) {
    const verdict = judgement?.verdict
        ? VERDICT_PRESENTATION[judgement.verdict]
        : undefined;

    return (
        <SectionCard>
            <SectionCardHeader
                title="Judge summary"
                subtitle={skillName}
                actions={
                    verdict && (
                        <Chip
                            size="small"
                            icon={verdict.icon}
                            color={verdict.color}
                            label={verdict.label}
                            sx={{ fontWeight: 700 }}
                        />
                    )
                }
            />
            <Box sx={{ px: 2.5, py: 2.5 }}>
                {running ? (
                    <Box sx={{ alignItems: "center", display: "flex", gap: 1.5 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                            Judging this edit against the original run…
                        </Typography>
                    </Box>
                ) : judgement?.success ? (
                    <Typography sx={{ fontSize: 15.5, lineHeight: 1.65 }}>
                        {judgement.summary}
                    </Typography>
                ) : (
                    <Typography color="error" variant="body2">
                        {judgement?.error ?? "The judge did not run."}
                    </Typography>
                )}
            </Box>
        </SectionCard>
    );
}

export function FlowStepDebugEvaluation({
    step,
    skills,
    drafts,
    result,
    service,
    sourceRunId,
    /** Bumped by the Skill Instructions "Evaluate outputs" button; judging runs on each new value. */
    judgeToken,
    onTryAnotherRun,
}: {
    step: FlowStep;
    skills: KboFlowStepDebugSkill[];
    drafts: SkillDrafts;
    result?: KboFlowStepDebugExecuteResult;
    service: KboFlowDebugService;
    sourceRunId?: string;
    judgeToken: number;
    onTryAnotherRun: () => void;
}) {
    const theme = useTheme();
    const [judgements, setJudgements] = useState<KboFlowStepDebugJudgement[]>([]);
    // Tracked per skill rather than as one flag: each judgement lands as soon as it returns, and a slow
    // or hung call can only leave its own card spinning.
    const [pendingSkills, setPendingSkills] = useState<Set<string>>(new Set());

    const editedSkills: EditedSkill[] = useMemo(
        () =>
            skills
                .filter(
                    (skill) =>
                        drafts[skill.id] !== undefined &&
                        drafts[skill.id] !== skill.skillText,
                )
                .map((skill) => ({
                    skill,
                    original: skill.skillText,
                    draft: drafts[skill.id],
                    edits: countEditedLines(skill.skillText, drafts[skill.id]),
                })),
        [skills, drafts],
    );

    const judge = useCallback(
        async (signal: AbortSignal) => {
            if (editedSkills.length === 0 || !result?.success) return;
            setJudgements([]);
            setPendingSkills(
                new Set(editedSkills.map((edited) => edited.skill.name ?? "")),
            );

            // One call per edited skill: each skill carries its own judge prompt server-side. They are
            // settled independently so one failure or stall cannot hold up the others.
            await Promise.all(
                editedSkills.map(async (edited) => {
                    const skillName = edited.skill.name ?? "";
                    let judgement: KboFlowStepDebugJudgement;
                    try {
                        judgement = await service.judgeStepDebug(
                            {
                                stepName: step.id,
                                stepLabel: step.label,
                                skillName,
                                originalSkillText: edited.original,
                                editedSkillText: edited.draft,
                                originalOutput: result.originalOutput,
                                producedOutput: result.output,
                            },
                            signal,
                        );
                    } catch (caught: unknown) {
                        judgement = {
                            skillName,
                            success: false,
                            error:
                                caught instanceof Error
                                    ? caught.message
                                    : "The judge could not be reached.",
                        };
                    }

                    if (signal.aborted) return;
                    setJudgements((current) => [...current, judgement]);
                    setPendingSkills((current) => {
                        const next = new Set(current);
                        next.delete(skillName);
                        return next;
                    });
                }),
            );
        },
        [editedSkills, result, service, step.id, step.label],
    );

    // Keyed on judgeToken alone: arriving via the Evaluate button bumps it, clicking the stage tab
    // does not, so simply returning to this tab never spends another model call.
    useEffect(() => {
        if (judgeToken === 0) return undefined;
        const controller = new AbortController();
        void judge(controller.signal);
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [judgeToken]);

    if (!result?.success) {
        return (
            <SectionCard>
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        Nothing to evaluate yet
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Run the step on Skill Instructions first — the judge compares
                        what it produced against the original run.
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    if (editedSkills.length === 0) {
        return (
            <SectionCard>
                <Box sx={{ p: 6, textAlign: "center" }}>
                    <Typography sx={{ fontWeight: 700 }}>
                        No skill edits to judge
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        The step ran with its saved instructions, so there is no change
                        to attribute a difference to.
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <SectionCard>
                <Box
                    sx={{
                        alignItems: { xs: "flex-start", md: "center" },
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 1.5,
                        px: 2.5,
                        py: 2,
                    }}
                >
                    <CompareArrowsRounded sx={{ color: "primary.main" }} />
                    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                        Comparing v{result.sourceRunVersion} → draft
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ color: theme.palette.gray[500] }}
                    >
                        {step.label}
                        {sourceRunId ? ` · run ${shortId(sourceRunId)} input` : ""}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip
                        size="small"
                        label="Judged by Evaluation Agent"
                        sx={{ fontWeight: 700 }}
                    />
                </Box>
            </SectionCard>

            <Box
                sx={{
                    alignItems: "flex-start",
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "minmax(320px, 1fr) minmax(0, 1.6fr)",
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    <Typography
                        component="h2"
                        sx={{
                            color: theme.palette.gray[600],
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                        }}
                    >
                        Skill changes
                    </Typography>
                    {editedSkills.map((edited) => (
                        <SkillChangesCard key={edited.skill.id} edited={edited} />
                    ))}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {editedSkills.map((edited) => (
                        <JudgeSummaryCard
                            key={edited.skill.id}
                            skillName={edited.skill.name ?? "Unnamed skill"}
                            judgement={judgements.find(
                                (j) => j.skillName === edited.skill.name,
                            )}
                            running={pendingSkills.has(
                                edited.skill.name ?? "",
                            )}
                        />
                    ))}

                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <AcxButton
                            color="white"
                            variant="outlined"
                            disabled={pendingSkills.size > 0}
                            onClick={onTryAnotherRun}
                        >
                            Try another run
                        </AcxButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
